import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// project import
import NavItem from "./NavItem";
import Transitions from "components/@extended/Transitions";
import { MenuOrientation, ThemeMode } from "config";

import useConfig from "hooks/useConfig";
import { useGetMenuMaster } from "api/menu";

// assets
import BorderOutlined from "@ant-design/icons/BorderOutlined";
import { ReactComponent as Chevron } from "assets/images/icons/icn_chevron_solid_light.svg";
import { ReactComponent as Chevron_solid } from "assets/images/icons/icn_chevron_solid.svg";
// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: "visible",
  zIndex: 1202,
  minWidth: 120,
  "& > .MuiBox-root": {
    position: "relative",
    "&:before": {
      content: '""',
      display: "block",
      position: "absolute",
      top: 26,
      ...(theme.direction !== "rtl" && { left: -12 }),
      ...(theme.direction === "rtl" && { right: -5 }),
      width: 10,
      height: 10,
      background: theme.palette.background.paper,
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 120,
      borderLeft: "2px solid",
      borderLeftColor: theme.palette.divider,
      borderBottom: "2px solid",
      borderBottomColor: theme.palette.divider,
    },
  },
  '&[data-popper-placement="right-end"]': {
    ".MuiPaper-root": {
      marginBottom: -8,
    },
    "&:before": {
      top: "auto",
      bottom: 5,
    },
  },
}));

export default function NavCollapse({
  menu,
  level,
  parentId,
  setSelectedItems,
  selectedItems,
  setSelectedLevel,
  selectedLevel,
}) {
  const theme = useTheme();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { mode, menuOrientation } = useConfig();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hovered, setHovered] = useState(false);

  const handleHover = (event) => {
    setHovered(true);
    setAnchorEl(event?.currentTarget);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setOpen(false);
    /* setAnchorEl(null); */
  };

  const handleIconClick = () => {
    setOpen((prev) => !prev);
    /* setSelected(menu.id); */
  };

  /* const miniMenuOpened = Boolean(anchorEl); */

  useMemo(() => {
    if (selected === selectedItems) {
      if (level === 1) {
        setOpen(true);
      }
    } else {
      if (level === selectedLevel) {
        setOpen(false);
        if (!open && !drawerOpen && !selected) {
          setSelected(null);
        }
        if (drawerOpen) {
          setSelected(null);
        }
      }
    }
  }, [
    selectedItems,
    level,
    selected,
    /* miniMenuOpened, */
    open,
    drawerOpen,
    selectedLevel,
  ]);

  const { pathname } = useLocation();

  const checkOpenForParent = (child, id) => {
    child.forEach((item) => {
      if (item.url === pathname) {
        setOpen(true);
        setSelected(id);
      }
    });
  };

  // menu collapse for sub-levels
  useEffect(() => {
    /* setOpen(false); */
    !open ? setSelected(null) : setAnchorEl(null);
    if (menu.children) {
      menu.children.forEach((item) => {
        if (item.children?.length) {
          checkOpenForParent(item.children, menu.id);
        }

        /* if (
          item.link &&
          !!matchPath({ path: item?.link, end: false }, pathname)
        ) {
          setSelected(menu.id);
          setOpen(true);
        } */

        if (item.url === pathname) {
          setSelected(menu.id);
          /* setOpen(true); */
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menu.children]);


  useEffect(() => {
    if (!drawerOpen) {
      setHovered(false);
      setAnchorEl(null);
    }
  }, [drawerOpen]);

  const navCollapse = menu.children?.map((item) => {
    switch (item.type) {
      case "collapse":
        return (
          <NavCollapse
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            menu={item}
            level={level + 1}
            parentId={parentId}
          />
        );
      case "item":
        return <NavItem key={item.id} item={item} level={level + 1} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Collapse or Item
          </Typography>
        );
    }
  });

  const isSelected = selected === menu.id;
  const textColor = mode === ThemeMode.DARK ? "grey.400" : "text.primary";
  const borderIcon =
    level === 1 ? <BorderOutlined style={{ fontSize: "1rem" }} /> : false;
  const Icon = isSelected ? menu.iconSelect : menu.icon;
  const menuIcon = menu.icon ? (
    <Icon style={{ fontSize: drawerOpen ? "1rem" : "1.25rem" }} />
  ) : (
    borderIcon
  );

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <>
          <ListItemButton
            id={`${menu.id}-button`}
            selected={isSelected}
            onMouseEnter={handleHover}
            onMouseLeave={handleMouseLeave}
            sx={{
              pl: drawerOpen ? `${level * 18}px` : 1.5,
              py: !drawerOpen && level === 1 ? 0.6 : 0.6,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                height: "0",
                width: "0",
                backgroundColor: "#8695cb",
                borderRadius: "30px",
                zIndex: -1,
              },
              ...(drawerOpen && {
                "&:hover": {
                  backgroundColor: "transparent !important",
                  boxShadow: "none",
                },
                "&:hover::before": {
                  height: "44px",
                  width: "270px",
                },
                "&.Mui-selected": {
                  backgroundColor: "transparent ",
                  boxShadow: "none ",
                  color: "#172664",
                },
                "&.Mui-selected::before": {
                  backgroundColor: "#ffffff",
                  height: "44px",
                  width: "270px",
                },
              }),
              ...(!drawerOpen && {
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": {
                  "&:hover": { bgcolor: "transparent" },
                  bgcolor: "transparent",
                },
              }),
            }}
          >
            {menuIcon && (
              <Tooltip title={menu.title} placement="right">
                <ListItemIcon
                  onClick={handleIconClick}
                  onMouseLeave={handleMouseLeave}
                  sx={{
                    minWidth: 28,
                    color: isSelected ? "#172664" : textColor,
                    ...(!drawerOpen && {
                      borderRadius: 30,
                      width: 44,
                      height: 44,
                      alignItems: "center",
                      justifyContent: "center",
                    }),
                    ...(!drawerOpen &&
                      isSelected && {
                        bgcolor: "#ffffff",
                      }),

                  }}
                >
                  {menuIcon}
                </ListItemIcon>
              </Tooltip>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: isSelected ? "#172664" : "#ffffff",
                      fontSize: "15px",
                      fontWeight: "bold",
                    }}
                  >
                    {menu.title}
                  </Typography>
                }
              />
            )}

            {isSelected ? (
              <Chevron_solid
                style={{
                  fontSize: "0.625rem",
                  marginLeft: 1,
                }}
              />
            ) : (
              <Chevron
                style={{
                  fontSize: "0.625rem",
                  marginLeft: 1,
                }}
              />
            )}
          </ListItemButton>
          {drawerOpen && (
            <PopperStyled
              open={hovered}
              anchorEl={anchorEl}
              placement="right-start"
              style={{ zIndex: 2001 }}
              popperOptions={{
                modifiers: [{ name: "offset", options: { offset: [-12, 1] } }],
              }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={handleMouseLeave}
            >
              {({ TransitionProps }) => (
                <Transitions in={hovered} {...TransitionProps}>
                  <Paper
                    sx={{
                      overflow: "hidden",
                      mt: 1.5,
                      mx: -1,
                      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
                      backgroundImage: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      height: "auto",
                      p: "10px",
                    }}
                  >
                    {navCollapse}
                  </Paper>
                </Transitions>
              )}
            </PopperStyled>
          )}
          {!drawerOpen && (
            <PopperStyled
              open={open}
              anchorEl={anchorEl}
              placement="right-start"
              style={{ zIndex: 2001 }}
              popperOptions={{
                modifiers: [{ name: "offset", options: { offset: [-12, 1] } }],
              }}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={handleMouseLeave}
            >
              {({ TransitionProps }) => (
                <Transitions in={open} {...TransitionProps}>
                  <Paper
                    sx={{
                      overflow: "hidden",
                      mt: 1.5,
                      mx: -1,
                      boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
                      backgroundImage: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      height: "auto",
                      p: "10px",
                    }}
                  >
                    {navCollapse}
                  </Paper>
                </Transitions>
              )}
            </PopperStyled>
          )}
        </>
      ) : null}
    </>
  );
}

NavCollapse.propTypes = {
  menu: PropTypes.any,
  level: PropTypes.number,
  parentId: PropTypes.string,
  setSelectedItems: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  selectedItems: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  setSelectedLevel: PropTypes.func,
  selectedLevel: PropTypes.number,
};
